import * as yup from "yup";
import { Constants } from "../../helpers/Constants";
import { ComplianceOptions } from "./Home/ApplicationDetails.types";

export type TankInstallationFormProps = {
  id: string;
  returnPath: string;
  title: string;
    applicationInspectionId?: number;
    applicationId?: number;
    values?: TankInstallationInput;
  action: "post" | "put";
};

export type TankInstallationInput = {
    tankInstallationId: number
    applicationId: number;
    applicationInspectionId: number;
    tankComplianceId: number;
    tankCompliance? : ComplianceOptions
    tankSizeComment: string;
    tankSize1: number;
    tankSize1Units: string;
    tankSize2: number;
    tankSize2Units: string;
    tankMaterialComplianceId: number;
    tankMaterialCompliance?: ComplianceOptions;
    tankMaterialComment: string;
    tankMaterialId1: number;
    tankMaterialId2: number;
    outletDeviceComplianceId: number;
    outletDeviceCompliance?: ComplianceOptions;
    outletDeviceComment: string;
    multiChamberedComplianceId: number;
    multiChamberedCompliance?: ComplianceOptions;
    multiChamberedComment: string;
    multiChambered: boolean;
    // New: Tank In Series fields
    inSeries: boolean;
    tank2InSeries: boolean;
    outletFilterComplienceId: number;
    outletFilterComplience?: ComplianceOptions;
    outletFilterComment: string;
    outletFilterId: number;
    legendComplianceId: number;
    legendCompliance?: ComplianceOptions;
    legendComment: string;
    legendId1: number;
    legendId2: number;
    watertightComplianceId: number;
    watertightCompliance?: ComplianceOptions;
    watertightComment: string;
    levelComplianceId: number;
    levelCompliance?: ComplianceOptions;
    levelComment: string;
    depthToLidComplianceId: number;
    depthToLidCompliance?: ComplianceOptions;
    depthToLidComment: string;
    depthToLidInches?: number;
    // New: Riser(s) fields
    risers: boolean;
    tank2Risers: boolean;
    inspectionStatusId: number;
    tank2ComplianceId: number;
    tank2Compliance?: ComplianceOptions;
    tank2SizeComment: string;
    tank2MaterialComplianceId: number;
    tank2MaterialCompliance?: ComplianceOptions;
    tank2MaterialComment: string;
    tank2LegendComplianceId: number;
    tank2LegendCompliance?: ComplianceOptions;
    tank2LegendComment: string;
    tank2OutletDeviceComplianceId: number;
    tank2OutletDeviceCompliance?: ComplianceOptions;
    tank2OutletDeviceComment: string;
    tank2MultiChamberedComplianceId: number;
    tank2MultiChamberedCompliance?: ComplianceOptions;
    tank2MultiChamberedComment: string;
    tank2MultiChambered: boolean;
    tank2OutletFilterComplianceId: number;
    tank2OutletFilterCompliance?: ComplianceOptions;
    tank2OutletFilterComment: string;
    tank2OutletFilterId: number;
    tank2WatertightComplianceId: number;
    tank2WatertightCompliance?: ComplianceOptions;
    tank2WatertightComment: string;
    tank2LevelComplianceId: number;
    tank2LevelCompliance?: ComplianceOptions;
    tank2LevelComment: string;
    tank2DepthToLidComplianceId: number;
    tank2DepthToLidCompliance?: ComplianceOptions;
    tank2DepthToLidComment: string;
    tank2DepthToLidInches?: number;
    // Combined ATU/PBTS field for Tank 1
    atuPbtComplianceId: number;
    atuPbtCompliance?: ComplianceOptions;
    atuPbtComment: string;
    atuPbtId: number;
    // Combined ATU/PBTS field for Tank 2
    tank2AtuPbtComplianceId: number;
    tank2AtuPbtCompliance?: ComplianceOptions;
    tank2AtuPbtComment: string;
    tank2AtuPbtId: number;
    // New: Tank Material Other Description fields
    tankMaterialOtherDescription: string;
    tank2MaterialOtherDescription: string;
    dateCreated: Date | string;
    createdBy: string;
    dateUpdated: Date;
    updatedBy: string;
    markAll: number;
    tankMaterialId1Navigation?: TankMaterialDto;
    tankMaterialId2Navigation?: TankMaterialDto;
    legendId1Navigation: LegendDto;
    legendId2Navigation:LegendDto;
    outletFilter:OutletFilterDto;
    isTank2Showable:boolean;
};

export type ComplianceData = {
  id: string;
  name: string;
};

export type TankMaterialDto = {
    tankMaterialId : number;
    tankMaterialValue : string;
    sortOrder? : number;
};

export type LegendDto= {
    legendId: number;
    legendValue?: string;
    sortOrder?: number;
}

export type OutletFilterDto = {
    outletFilterId: number;
    outletFilterValue?: string;
    sortOrder?: number;
}

export type TankMaterialData = {
    id: string;
    name: string;
};

export type LegendData = {
    id: string;
    name: string;
};

export type OutletFilterData = {
    id: string;
    name: string;
};

export type AtuPbtData = {
    id: string | number;
    name: string;
};

export const schema = yup.object().shape({

    tankComplianceId: yup
        .number()
        .min(1, "Tank Size Compliance is required")
        .required("Tank Size Compliance is required")
        .typeError("Tank Size Compliance is required"),
    tankSize1: yup.number().nullable(true).transform((value) => Number.isNaN(value)?null:value)
    .when("tankComplianceId", {
      is: (val: any) => (val != Constants.Not_Applicable &&  val != Constants.Unobserved),
      then:yup.number()
        .integer("Tank Size must be an Integer")
        .min(1, "Tank Size (gal/gpd) must be greater than 0")       
        .required("Tank Size (gal/gpd) is required")
        .typeError("Tank Size (gal/gpd)  is required"),
      otherwise: yup.number().nullable().notRequired()
    }),
    tankSize1Units: yup.string().nullable(true)
        .when("tankComplianceId", {
            is: (val: any) => (val != Constants.Not_Applicable && val != Constants.Unobserved),
            then: yup.string()
                .required("Tank Size Units is required")
                .typeError("Tank Size Units is required"),
            otherwise: yup.string().nullable().notRequired()
        }),
    tankSizeComment: yup.string().nullable(true)
        .when("tankComplianceId", {
            is: (val: any) => val === Constants.Out_of_Compliance,
            then: yup.string()
                .required("Tank Size Compliance Comment is required")
                .typeError("Tank Size ComplianceComment is required"),
            otherwise: yup.string().nullable().notRequired()
        }),

    tankMaterialComplianceId: yup
        .number()
        .min(1, "Tank Material Compliance is required")
        .required("Tank Material Compliance is required")
        .typeError("Tank Material Compliance is required"),
    tankMaterialId1: yup.mixed()
        .transform((val, orig) => {
            if (orig === '' || orig === undefined || orig === null) return undefined;
            if (typeof orig === 'string') {
                const trimmed = orig.trim();
                if (trimmed === '') return undefined;
                const num = Number(trimmed);
                return Number.isNaN(num) ? undefined : num;
            }
            if (typeof orig === 'number') {
                return Number.isNaN(orig) ? undefined : orig;
            }
            return undefined;
        })
        .when("tankMaterialComplianceId", {
            is: (val: any) => (val != Constants.Not_Applicable && val != Constants.Unobserved),
            then: yup.number()
                .test('material-type-or-other', 'Tank Material Type is required when not selecting Other', function(value) {
                    // Allow -1 (Other) or any positive number
                    return value === -1 || (typeof value === 'number' && value > 0);
                })
                .required("Tank Material Type is required")
                .typeError("Tank Material Type is required"),
            otherwise: yup.mixed().nullable().notRequired()
        }),
    tankMaterialComment: yup.string().nullable(true)
        .when("tankMaterialComplianceId", {
            is: (val: any) => val === Constants.Out_of_Compliance,
            then: yup.string()
                .required("Tank Material Type Comment is required")
                .typeError("Tank Material Type Comment is required"),
            otherwise: yup.string().nullable().notRequired()
        }),
    // Tank Material Other Description validation
    tankMaterialOtherDescription: yup.string().nullable(true)
        .when('tankMaterialId1', {
            is: (tankMaterialId1: number) => tankMaterialId1 === -1, // -1 represents "Other" option
            then: yup.string()
                .required("Tank Material Other Description is required")
                .typeError("Tank Material Other Description is required"),
            otherwise: yup.string().nullable().notRequired()
        }),

    outletDeviceComplianceId: yup
        .number()
        .min(1, "Tank Outlet Device Compliance is required")
        .required("Tank Outlet Device Compliance is required")
        .typeError("Tank Outlet Device Compliance is required"),
  
    outletDeviceComment: yup.string().nullable(true)
        .when("outletDeviceComplianceId", {
            is: (val: any) => val === Constants.Out_of_Compliance,
            then: yup.string()
                .required("Tank Outlet Device Comment is required")
                .typeError("Tank Outlet Device Comment is required"),
            otherwise: yup.string().nullable().notRequired()
        }),

    multiChamberedComplianceId: yup
        .number()
        .min(1, "Tank MultiChambered Compliance is required")
        .required("Tank MultiChambered Compliance is required")
        .typeError("Tank MultiChambered Compliance is required"),
    multiChambered: yup
        .boolean()
        .transform((val, orig) => {
            if (orig === '' || orig === undefined || orig === null) return undefined as any;
            return Number(orig) === 1;
        })
        .nullable(true)
        .when("multiChamberedComplianceId", {
            is: (val: any) => (val != Constants.Not_Applicable && val != Constants.Unobserved),
            then: yup.boolean()
                .required("Tank Multichambered is required")
                .typeError("Tank Multichambered is required"),
            otherwise: yup.boolean().nullable().notRequired()
        }),
    inSeries: yup
        .boolean()
        .transform((val, orig) => {
            if (orig === '' || orig === undefined || orig === null) return undefined as any;
            return Number(orig) === 1;
        })
        .nullable(true)
        .when("multiChamberedComplianceId", {
            is: (val: any) => (val != Constants.Not_Applicable && val != Constants.Unobserved),
            then: yup.boolean()
                .required("Tank In Series is required")
                .typeError("Tank In Series is required"),
            otherwise: yup.boolean().nullable().notRequired()
        }),
    multiChamberedComment: yup.string().nullable(true)
        .when("multiChamberedComplianceId", {
            is: (val: any) => val === Constants.Out_of_Compliance,
            then: yup.string()
                .required("Tank MultiChambered Comment is required")
                .typeError("Tank MultiChambered Comment is required"),
            otherwise: yup.string().nullable().notRequired()
        }),

    outletFilterComplienceId: yup
        .number()
        .min(1, "Tank Outlet Filter Compliance is required")
        .required("Tank Outlet Filter Compliance is required")
        .typeError("Tank Outlet Filter Compliance is required"),
    outletFilterId: yup.mixed()
        .transform((val, orig) => {
            if (orig === '' || orig === undefined || orig === null) return undefined;
            if (typeof orig === 'string') {
                const trimmed = orig.trim();
                if (trimmed === '') return undefined;
                const num = Number(trimmed);
                return Number.isNaN(num) ? undefined : num;
            }
            if (typeof orig === 'number') {
                return Number.isNaN(orig) ? undefined : orig;
            }
            return undefined;
        })
        .when("outletFilterComplienceId", {
            is: (val: any) => (val != Constants.Not_Applicable && val != Constants.Out_of_Compliance && val != Constants.Unobserved),
            then: yup.number()
                .min(1, "Tank Outlet Filter Type is required")
                .required("Tank Outlet Filter Type is required")
                .typeError("Tank Outlet Filter Type is required"),
            otherwise: yup.mixed().nullable().notRequired()
        }),
    outletFilterComment: yup.string().nullable(true)
        .when("outletFilterComplienceId", {
            is: (val: any) => val === Constants.Out_of_Compliance,
            then: yup.string()
                .required("Tank Outlet Filter Type Comment is required")
                .typeError("Tank Outlet Filter Type Comment is required"),
            otherwise: yup.string().nullable().notRequired()
        }),
  
    legendComplianceId: yup
        .number()
        .min(1, "Tank Legend Compliance is required")
        .required("Tank Legend Compliance is required")
        .typeError("Tank Legend Compliance is required"),
    legendId1: yup.number()
        .min(1, "Tank Legend Type is required")
        .required("Tank Legend Type is required")
        .typeError("Tank Legend Type is required"),
    legendComment: yup.string().nullable(true)
        .when("legendComplianceId", {
            is: (val: any) => val === Constants.Out_of_Compliance,
            then: yup.string()
                .required("Tank Legend Type Comment is required")
                .typeError("Tank Legend Type Comment is required"),
            otherwise: yup.string().nullable().notRequired()
        }),
   
    watertightComplianceId: yup
        .number()
        .min(1, "Tank Watertight Compliance is required")
        .required("Tank Watertight Compliance is required")
        .typeError("Tank Watertight Compliance is required"),
   
    watertightComment: yup.string().nullable(true)
        .when("watertightComplianceId", {
            is: (val: any) => val === Constants.Out_of_Compliance,
            then: yup.string()
                .required("Tank Watertight Comment is required")
                .typeError("Tank Watertight Comment is required"),
            otherwise: yup.string().nullable().notRequired()
        }),

    levelComplianceId: yup
        .number()
        .min(1, "Tank Level Compliance is required")
        .required("Tank Level Compliance is required")
        .typeError("Tank Level Compliance is required"),

    levelComment: yup.string().nullable(true)
        .when("levelComplianceId", {
            is: (val: any) => val === Constants.Out_of_Compliance,
            then: yup.string()
                .required("Tank Level Comment is required")
                .typeError("Tank Level Comment is required"),
            otherwise: yup.string().nullable().notRequired()
        }),

    depthToLidComplianceId: yup
        .number()
        .min(1, "Tank Depth To Lid Compliance is required")
        .required("Tank Depth To Lid Compliance is required")
        .typeError("Tank Depth To Lid Compliance is required"),

    depthToLidComment: yup.string().nullable(true)
        .when("depthToLidComplianceId", {
            is: (val: any) => val === Constants.Out_of_Compliance,
            then: yup.string()
                .required("Tank Depth To Lid Comment is required")
                .typeError("Tank Depth To Lid Comment is required"),
            otherwise: yup.string().nullable().notRequired()
        }),

    // Depth to Lid Inches (Tank 1)
    depthToLidInches: yup.mixed()
        .transform((val, orig) => {
            if (orig === '' || orig === undefined || orig === null) return undefined;
            const num = Number(orig);
            return Number.isNaN(num) ? undefined : num;
        })
        .when("depthToLidComplianceId", {
            is: (val: any) => (val != Constants.Not_Applicable && val != Constants.Unobserved),
            then: yup.number()
                .min(0, "Depth to Lid (inches) must be zero or more")
                .required("Depth to Lid (inches) is required")
                .typeError("Depth to Lid (inches) is required"),
            otherwise: yup.mixed().nullable().notRequired()
        }),

    risers: yup.boolean().nullable(true)
        .transform((value) => {
            if (value === '' || value === undefined || value === null) return undefined;
            if (typeof value === 'string') return Number(value) === 1;
            if (typeof value === 'number') return value === 1;
            return value;
        })
        .when("depthToLidComplianceId", {
            is: (val: any) => val != Constants.Not_Applicable && val != Constants.Unobserved,
            then: yup.boolean()
                .required("Riser(s) is required")
                .typeError("Riser(s) is required"),
            otherwise: yup.boolean().nullable().notRequired()
        }),

    /*=======================================================================================*/
    tank2ComplianceId: yup
        .number().nullable(true)
        .when("isTank2Showable", {
            is: true,
            then: yup.number()
                .min(1, "Tank 2 Size Compliance is required")
                .required("Tank 2 Size Compliance is required")
                .typeError("Tank 2 Size Compliance is required"),
            otherwise: yup.number().nullable().notRequired()
        }),

    tankSize2: yup.number().nullable(true)
        .when(['isTank2Showable', 'tank2ComplianceId'], {
            is: (isTank2Showable: boolean, tank2ComplianceId: number) => 
                isTank2Showable && tank2ComplianceId != Constants.Not_Applicable && tank2ComplianceId != Constants.Unobserved,
            then: yup.number()
                .integer("Tank 2 Size must be an Integer")
                .min(1, "Tank 2 Size (gal/gpd) must be greater than 0")
                .required("Tank 2 Size (gal/gpd) is required")
                .typeError("Tank 2 Size (gal/gpd) is required"),
            otherwise: yup.number().nullable().notRequired()
        }),

    tankSize2Units: yup.string().nullable(true)
        .when(['isTank2Showable', 'tank2ComplianceId'], {
            is: (isTank2Showable: boolean, tank2ComplianceId: number) => 
                isTank2Showable && tank2ComplianceId != Constants.Not_Applicable && tank2ComplianceId != Constants.Unobserved,
            then: yup.string()
                .required("Tank 2 Size Units is required")
                .typeError("Tank 2 Size Units is required"),
            otherwise: yup.string().nullable().notRequired()
        }),

    tank2SizeComment: yup.string().nullable(true)
        .when(['isTank2Showable', 'tank2ComplianceId'], {
            is: (isTank2Showable: boolean, tank2ComplianceId: number) => 
                isTank2Showable && tank2ComplianceId === Constants.Out_of_Compliance,
            then: yup.string()
                .required("Tank 2 Size Compliance Comment is required")
                .typeError("Tank 2 Size Compliance Comment is required"),
            otherwise: yup.string().nullable().notRequired()
        }),

    tank2MaterialComplianceId: yup
        .number().nullable(true)
        .when("isTank2Showable", {
            is: true,
            then: yup.number()
                .min(1, "Tank 2 Material Compliance is required")
                .required("Tank 2 Material Compliance is required")
                .typeError("Tank 2 Material Compliance is required"),
            otherwise: yup.number().nullable().notRequired()
        }),

    tankMaterialId2: yup.mixed()
        .transform((val, orig) => {
            if (orig === '' || orig === undefined || orig === null) return undefined;
            if (typeof orig === 'string') {
                const trimmed = orig.trim();
                if (trimmed === '') return undefined;
                const num = Number(trimmed);
                return Number.isNaN(num) ? undefined : num;
            }
            if (typeof orig === 'number') {
                return Number.isNaN(orig) ? undefined : orig;
            }
            return undefined;
        })
        .when(['isTank2Showable', 'tank2MaterialComplianceId'], {
            is: (isTank2Showable: boolean, tank2MaterialComplianceId: number) => 
                isTank2Showable && tank2MaterialComplianceId != Constants.Not_Applicable && tank2MaterialComplianceId != Constants.Unobserved,
            then: yup.number()
                .test('tank2-material-type-or-other', 'Tank 2 Material Type is required when not selecting Other', function(value) {
                    // Allow -1 (Other) or any positive number
                    return value === -1 || (typeof value === 'number' && value > 0);
                })
                .required("Tank 2 Material Type is required")
                .typeError("Tank 2 Material Type is required"),
            otherwise: yup.mixed().nullable().notRequired()
        }),

    tank2MaterialComment: yup.string().nullable(true)
        .when(['isTank2Showable', 'tank2MaterialComplianceId'], {
            is: (isTank2Showable: boolean, tank2MaterialComplianceId: number) => 
                isTank2Showable && tank2MaterialComplianceId === Constants.Out_of_Compliance,
            then: yup.string()
                .required("Tank 2 Material Type Comment is required")
                .typeError("Tank 2 Material Type Comment is required"),
            otherwise: yup.string().nullable().notRequired()
        }),
    // Tank 2 Material Other Description validation
    tank2MaterialOtherDescription: yup.string().nullable(true)
        .when(['isTank2Showable', 'tankMaterialId2'], {
            is: (isTank2Showable: boolean, tankMaterialId2: number) => 
                isTank2Showable && 
                tankMaterialId2 === -1, // -1 represents "Other" option
            then: yup.string()
                .required("Tank 2 Material Other Description is required")
                .typeError("Tank 2 Material Other Description is required"),
            otherwise: yup.string().nullable().notRequired()
        }),

    tank2LegendComplianceId: yup
        .number().nullable(true)
        .when("isTank2Showable", {
            is: true,
            then: yup.number()
                .min(1, "Tank 2 Legend Compliance is required")
                .required("Tank 2 Legend Compliance is required")
                .typeError("Tank 2 Legend Compliance is required"),
            otherwise: yup.number().nullable().notRequired()
        }),

    legendId2: yup.number().nullable(true)
        .when(['isTank2Showable', 'tank2LegendComplianceId'], {
            is: (isTank2Showable: boolean, tank2LegendComplianceId: number) => 
                isTank2Showable && tank2LegendComplianceId != Constants.Not_Applicable && tank2LegendComplianceId != Constants.Unobserved,
            then: yup.number()
                .min(1, "Tank 2 Legend Type is required")
                .required("Tank 2 Legend Type is required")
                .typeError("Tank 2 Legend Type is required"),
            otherwise: yup.number().nullable().notRequired()
        }),

    tank2LegendComment: yup.string().nullable(true)
        .when(['isTank2Showable', 'tank2LegendComplianceId'], {
            is: (isTank2Showable: boolean, tank2LegendComplianceId: number) => 
                isTank2Showable && tank2LegendComplianceId === Constants.Out_of_Compliance,
            then: yup.string()
                .required("Tank 2 Legend Type Comment is required")
                .typeError("Tank 2 Legend Type Comment is required"),
            otherwise: yup.string().nullable().notRequired()
        }),

    tank2OutletDeviceComplianceId: yup.number().nullable(true)
        .when("isTank2Showable", {
            is: true,
            then: yup.number()
                .min(1, "Tank 2 Outlet Device Compliance is required")
                .required("Tank 2 Outlet Device Compliance is required")
                .typeError("Tank 2 Outlet Device Compliance is required"),
            otherwise: yup.number().nullable().notRequired()
        }),

    tank2OutletDeviceComment: yup.string().nullable(true)
        .when(['isTank2Showable', 'tank2OutletDeviceComplianceId'], {
            is: (isTank2Showable: boolean, tank2OutletDeviceComplianceId: number) => 
                isTank2Showable && tank2OutletDeviceComplianceId === Constants.Out_of_Compliance,
            then: yup.string()
                .required("Tank 2 Outlet Device Comment is required")
                .typeError("Tank 2 Outlet Device Comment is required"),
            otherwise: yup.string().nullable().notRequired()
        }),

    tank2MultiChamberedComplianceId: yup.number().nullable(true)
        .when("isTank2Showable", {
            is: true,
            then: yup.number()
                .min(1, "Tank 2 Multichambered Compliance is required")
                .required("Tank 2 Multichambered Compliance is required")
                .typeError("Tank 2 Multichambered Compliance is required"),
            otherwise: yup.number().nullable().notRequired()
        }),

    tank2MultiChambered: yup
        .boolean()
        .transform((val, orig) => {
            if (orig === '' || orig === undefined || orig === null) return undefined as any;
            return Number(orig) === 1;
        })
        .nullable(true)
        .when(['isTank2Showable', 'tank2MultiChamberedComplianceId'], {
            is: (isTank2Showable: boolean, tank2MultiChamberedComplianceId: number) => 
                isTank2Showable && tank2MultiChamberedComplianceId != Constants.Not_Applicable && tank2MultiChamberedComplianceId != Constants.Unobserved,
            then: yup.boolean()
                .required("Tank 2 Multichambered is required")
                .typeError("Tank 2 Multichambered is required"),
            otherwise: yup.boolean().nullable().notRequired()
        }),
    tank2InSeries: yup
        .boolean()
        .transform((val, orig) => {
            if (orig === '' || orig === undefined || orig === null) return undefined as any;
            return Number(orig) === 1;
        })
        .nullable(true)
        .when(['isTank2Showable', 'tank2MultiChamberedComplianceId'], {
            is: (isTank2Showable: boolean, tank2MultiChamberedComplianceId: number) => 
                isTank2Showable && tank2MultiChamberedComplianceId != Constants.Not_Applicable && tank2MultiChamberedComplianceId != Constants.Unobserved,
            then: yup.boolean()
                .required("Tank 2 In Series is required")
                .typeError("Tank 2 In Series is required"),
            otherwise: yup.boolean().nullable().notRequired()
        }),

    tank2MultiChamberedComment: yup.string().nullable(true)
        .when(['isTank2Showable', 'tank2MultiChamberedComplianceId'], {
            is: (isTank2Showable: boolean, tank2MultiChamberedComplianceId: number) => 
                isTank2Showable && tank2MultiChamberedComplianceId === Constants.Out_of_Compliance,
            then: yup.string()
                .required("Tank 2 Multichambered Comment is required")
                .typeError("Tank 2 Multichambered Comment is required"),
            otherwise: yup.string().nullable().notRequired()
        }),

    tank2OutletFilterComplianceId: yup.number().nullable(true)
        .when("isTank2Showable", {
            is: true,
            then: yup.number()
                .min(1, "Tank 2 Outlet Filter Compliance is required")
                .required("Tank 2 Outlet Filter Compliance is required")
                .typeError("Tank 2 Outlet Filter Compliance is required"),
            otherwise: yup.number().nullable().notRequired()
        }),

    tank2OutletFilterId: yup.mixed()
        .transform((val, orig) => {
            if (orig === '' || orig === undefined || orig === null) return undefined;
            if (typeof orig === 'string') {
                const trimmed = orig.trim();
                if (trimmed === '') return undefined;
                const num = Number(trimmed);
                return Number.isNaN(num) ? undefined : num;
            }
            if (typeof orig === 'number') {
                return Number.isNaN(orig) ? undefined : orig;
            }
            return undefined;
        })
        .when(['isTank2Showable', 'tank2OutletFilterComplianceId'], {
            is: (isTank2Showable: boolean, tank2OutletFilterComplianceId: number) => 
                isTank2Showable && tank2OutletFilterComplianceId != Constants.Not_Applicable && tank2OutletFilterComplianceId != Constants.Out_of_Compliance && tank2OutletFilterComplianceId != Constants.Unobserved,
            then: yup.number()
                .min(1, "Tank 2 Outlet Filter Type is required")
                .required("Tank 2 Outlet Filter Type is required")
                .typeError("Tank 2 Outlet Filter Type is required"),
            otherwise: yup.mixed().nullable().notRequired()
        }),

    tank2OutletFilterComment: yup.string().nullable(true)
        .when(['isTank2Showable', 'tank2OutletFilterComplianceId'], {
            is: (isTank2Showable: boolean, tank2OutletFilterComplianceId: number) => 
                isTank2Showable && tank2OutletFilterComplianceId === Constants.Out_of_Compliance,
            then: yup.string()
                .required("Tank 2 Outlet Filter Comment is required")
                .typeError("Tank 2 Outlet Filter Comment is required"),
            otherwise: yup.string().nullable().notRequired()
        }),

    tank2WatertightComplianceId: yup.number().nullable(true)
        .when("isTank2Showable", {
            is: true,
            then: yup.number()
                .min(1, "Tank 2 Watertight Compliance is required")
                .required("Tank 2 Watertight Compliance is required")
                .typeError("Tank 2 Watertight Compliance is required"),
            otherwise: yup.number().nullable().notRequired()
        }),

    tank2WatertightComment: yup.string().nullable(true)
        .when(['isTank2Showable', 'tank2WatertightComplianceId'], {
            is: (isTank2Showable: boolean, tank2WatertightComplianceId: number) => 
                isTank2Showable && tank2WatertightComplianceId === Constants.Out_of_Compliance,
            then: yup.string()
                .required("Tank 2 Watertight Comment is required")
                .typeError("Tank 2 Watertight Comment is required"),
            otherwise: yup.string().nullable().notRequired()
        }),

    tank2LevelComplianceId: yup.number().nullable(true)
        .when("isTank2Showable", {
            is: true,
            then: yup.number()
                .min(1, "Tank 2 Level Compliance is required")
                .required("Tank 2 Level Compliance is required")
                .typeError("Tank 2 Level Compliance is required"),
            otherwise: yup.number().nullable().notRequired()
        }),

    tank2LevelComment: yup.string().nullable(true)
        .when(['isTank2Showable', 'tank2LevelComplianceId'], {
            is: (isTank2Showable: boolean, tank2LevelComplianceId: number) => 
                isTank2Showable && tank2LevelComplianceId === Constants.Out_of_Compliance,
            then: yup.string()
                .required("Tank 2 Level Comment is required")
                .typeError("Tank 2 Level Comment is required"),
            otherwise: yup.string().nullable().notRequired()
        }),

    tank2DepthToLidComplianceId: yup.number().nullable(true)
        .when("isTank2Showable", {
            is: true,
            then: yup.number()
                .min(1, "Tank 2 Depth to Lid Compliance is required")
                .required("Tank 2 Depth to Lid Compliance is required")
                .typeError("Tank 2 Depth to Lid Compliance is required"),
            otherwise: yup.number().nullable().notRequired()
        }),

    tank2DepthToLidComment: yup.string().nullable(true)
        .when(['isTank2Showable', 'tank2DepthToLidComplianceId'], {
            is: (isTank2Showable: boolean, tank2DepthToLidComplianceId: number) => 
                isTank2Showable && tank2DepthToLidComplianceId === Constants.Out_of_Compliance,
            then: yup.string()
                .required("Tank 2 Depth to Lid Comment is required")
                .typeError("Tank 2 Depth to Lid Comment is required"),
            otherwise: yup.string().nullable().notRequired()
        }),

    // Depth to Lid Inches (Tank 2)
    tank2DepthToLidInches: yup.mixed()
        .transform((val, orig) => {
            if (orig === '' || orig === undefined || orig === null) return undefined;
            const num = Number(orig);
            return Number.isNaN(num) ? undefined : num;
        })
        .when(['isTank2Showable', 'tank2DepthToLidComplianceId'], {
            is: (isTank2Showable: boolean, tank2DepthToLidComplianceId: number) => 
                isTank2Showable && tank2DepthToLidComplianceId != Constants.Not_Applicable && tank2DepthToLidComplianceId != Constants.Unobserved,
            then: yup.number()
                .min(0, "Tank 2 Depth to Lid (inches) must be zero or more")
                .required("Tank 2 Depth to Lid (inches) is required")
                .typeError("Tank 2 Depth to Lid (inches) is required"),
            otherwise: yup.mixed().nullable().notRequired()
        }),

    tank2Risers: yup.boolean().nullable(true)
        .transform((value) => {
            if (value === '' || value === undefined || value === null) return undefined;
            if (typeof value === 'string') return Number(value) === 1;
            if (typeof value === 'number') return value === 1;
            return value;
        })
        .when(['isTank2Showable', 'tank2DepthToLidComplianceId'], {
            is: (isTank2Showable: boolean, tank2DepthToLidComplianceId: number) => 
                isTank2Showable && tank2DepthToLidComplianceId != Constants.Not_Applicable && tank2DepthToLidComplianceId != Constants.Unobserved,
            then: yup.boolean()
                .required("Tank 2 Riser(s) is required")
                .typeError("Tank 2 Riser(s) is required"),
            otherwise: yup.boolean().nullable().notRequired()
        }),

    // Combined ATU/PBTS validation for Tank 1
    atuPbtComplianceId: yup
        .number()
        .min(1, "Tank ATU/PBTS Compliance is required")
        .required("Tank ATU/PBTS Compliance is required")
        .typeError("Tank ATU/PBTS Compliance is required"),
    
    atuPbtId: yup.mixed()
        .transform((val, orig) => {
            if (orig === '' || orig === undefined || orig === null) return undefined;
            if (typeof orig === 'string') {
                const trimmed = orig.trim();
                if (trimmed === '') return undefined;
                const num = Number(trimmed);
                return Number.isNaN(num) ? undefined : num;
            }
            if (typeof orig === 'number') {
                return Number.isNaN(orig) ? undefined : orig;
            }
            return undefined;
        })
        .when("atuPbtComplianceId", {
            is: (val: any) => (val != Constants.Not_Applicable && val != Constants.Unobserved),
            then: yup.number()
                .min(1, "ATU/PBTS Make and Model is required")
                .required("ATU/PBTS Make and Model is required")
                .typeError("ATU/PBTS Make and Model is required"),
            otherwise: yup.mixed().nullable().notRequired()
        }),
    
    atuPbtComment: yup.string().nullable(true)
        .when("atuPbtComplianceId", {
            is: (val: any) => val === Constants.Out_of_Compliance,
            then: yup.string()
                .required("Tank ATU/PBTS Comment is required")
                .typeError("Tank ATU/PBTS Comment is required"),
            otherwise: yup.string().nullable().notRequired()
        }),

    // Combined ATU/PBTS validation for Tank 2
    tank2AtuPbtComplianceId: yup.number().nullable(true)
        .when("isTank2Showable", {
            is: true,
            then: yup.number()
                .min(1, "Tank 2 ATU/PBTS Compliance is required")
                .required("Tank 2 ATU/PBTS Compliance is required")
                .typeError("Tank 2 ATU/PBTS Compliance is required"),
            otherwise: yup.number().nullable().notRequired()
        }),

    tank2AtuPbtId: yup.number().nullable(true)
        .when(['isTank2Showable', 'tank2AtuPbtComplianceId'], {
            is: (isTank2Showable: boolean, tank2AtuPbtComplianceId: number) => 
                isTank2Showable && tank2AtuPbtComplianceId != Constants.Not_Applicable && tank2AtuPbtComplianceId != Constants.Unobserved,
            then: yup.number()
                .min(1, "Tank 2 ATU/PBTS Make and Model is required")
                .required("Tank 2 ATU/PBTS Make and Model is required")
                .typeError("Tank 2 ATU/PBTS Make and Model is required"),
            otherwise: yup.number().nullable().notRequired()
        }),

    tank2AtuPbtComment: yup.string().nullable(true)
        .when(['isTank2Showable', 'tank2AtuPbtComplianceId'], {
            is: (isTank2Showable: boolean, tank2AtuPbtComplianceId: number) => 
                isTank2Showable && tank2AtuPbtComplianceId === Constants.Out_of_Compliance,
            then: yup.string()
                .required("Tank 2 ATU/PBTS Comment is required")
                .typeError("Tank 2 ATU/PBTS Comment is required"),
            otherwise: yup.string().nullable().notRequired()
        }),
});
/*=======================================================================================*/